import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Plus, Edit, Trash2, X, DollarSign, Users, Baby, ChevronDown, ChevronUp, Upload, Link as LinkIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { roomTypesApi, childPricingApi, uploadApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const PRICING_TYPES = [
  { value: 'FREE', label: 'Free (No charge)' },
  { value: 'FIXED', label: 'Fixed Rate' },
  { value: 'ADULT_RATE', label: 'Extra Adult Rate' },
  { value: 'NOT_ALLOWED', label: 'Not Allowed' }
];

const AdminRoomTypesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roomTypes, setRoomTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const [childRules, setChildRules] = useState({});
  const [showChildRuleForm, setShowChildRuleForm] = useState(null);
  const [editingChildRule, setEditingChildRule] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const imageFileRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    base_rate_single: '',
    extra_adult_rate: '',
    max_adults: 2,
    max_occupancy: 4,
    sort_order: 0,
    is_active: true
  });

  const [childRuleForm, setChildRuleForm] = useState({
    min_age: 0,
    max_age: 17,
    pricing_type: 'FREE',
    rate_value: 0
  });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setImageUploadError(`File too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Max 5MB.`);
      return;
    }

    try {
      setImageUploading(true);
      setImageUploadError(null);
      const result = await uploadApi.uploadImage(file, token);
      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, image_url: result.url }));
      } else {
        setImageUploadError(result.detail || 'Upload failed');
      }
    } catch (err) {
      setImageUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setImageUploading(false);
      if (imageFileRef.current) imageFileRef.current.value = '';
    }
  };

  const fetchRoomTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      if (!authToken) {
        navigate('/login');
        return;
      }
      
      setToken(authToken);
      const data = await roomTypesApi.getAll(false);
      setRoomTypes(data);
    } catch (err) {
      console.error('Error fetching room types:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to load room types",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  const fetchChildRules = useCallback(async (roomTypeId) => {
    try {
      const rules = await childPricingApi.getByRoomType(roomTypeId);
      setChildRules(prev => ({ ...prev, [roomTypeId]: rules }));
    } catch (err) {
      console.error('Error fetching child rules:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchRoomTypes();
  }, [isAuthenticated, navigate, fetchRoomTypes]);

  const toggleExpand = (roomTypeId) => {
    if (expandedType === roomTypeId) {
      setExpandedType(null);
    } else {
      setExpandedType(roomTypeId);
      if (!childRules[roomTypeId]) {
        fetchChildRules(roomTypeId);
      }
    }
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      base_rate_single: '',
      extra_adult_rate: '',
      max_adults: 2,
      max_occupancy: 4,
      sort_order: 0,
      is_active: true
    });
    setShowForm(true);
  };

  const handleEdit = (roomType) => {
    setEditing(roomType.id);
    setFormData({
      name: roomType.name || '',
      description: roomType.description || '',
      image_url: roomType.image_url || '',
      base_rate_single: roomType.base_rate_single || '',
      extra_adult_rate: roomType.extra_adult_rate || '',
      max_adults: roomType.max_adults || 2,
      max_occupancy: roomType.max_occupancy || roomType.max_adults || 4,
      sort_order: roomType.sort_order || 0,
      is_active: roomType.is_active !== false
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const data = {
        name: formData.name,
        description: formData.description,
        image_url: formData.image_url || null,
        base_rate_single: parseFloat(formData.base_rate_single) || 0,
        extra_adult_rate: parseFloat(formData.extra_adult_rate) || 0,
        max_adults: parseInt(formData.max_adults) || 2,
        max_occupancy: parseInt(formData.max_occupancy) || parseInt(formData.max_adults) || 4,
        sort_order: parseInt(formData.sort_order) || 0,
        is_active: formData.is_active
      };
      
      if (editing) {
        await roomTypesApi.update(editing, data, token);
        toast({ title: "Success", description: "Room type updated successfully" });
      } else {
        await roomTypesApi.create(data, token);
        toast({ title: "Success", description: "Room type created successfully" });
      }
      
      setShowForm(false);
      setEditing(null);
      fetchRoomTypes();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save room type",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this room type?')) return;
    
    try {
      await roomTypesApi.delete(id, token);
      toast({ title: "Success", description: "Room type deleted successfully" });
      fetchRoomTypes();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete room type",
        variant: "destructive"
      });
    }
  };

  // Child Pricing Rule handlers
  const handleAddChildRule = (roomTypeId) => {
    setShowChildRuleForm(roomTypeId);
    setEditingChildRule(null);
    setChildRuleForm({
      min_age: 0,
      max_age: 17,
      pricing_type: 'FREE',
      rate_value: 0
    });
  };

  const handleEditChildRule = (rule) => {
    setShowChildRuleForm(rule.room_type_id);
    setEditingChildRule(rule.id);
    setChildRuleForm({
      min_age: rule.min_age,
      max_age: rule.max_age,
      pricing_type: rule.pricing_type,
      rate_value: rule.rate_value || 0
    });
  };

  const handleCancelChildRule = () => {
    setShowChildRuleForm(null);
    setEditingChildRule(null);
  };

  const handleSubmitChildRule = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const data = {
        room_type_id: showChildRuleForm,
        min_age: parseInt(childRuleForm.min_age),
        max_age: parseInt(childRuleForm.max_age),
        pricing_type: childRuleForm.pricing_type,
        rate_value: parseFloat(childRuleForm.rate_value) || 0
      };
      
      if (editingChildRule) {
        await childPricingApi.update(editingChildRule, data, token);
        toast({ title: "Success", description: "Child pricing rule updated" });
      } else {
        await childPricingApi.create(data, token);
        toast({ title: "Success", description: "Child pricing rule created" });
      }
      
      setShowChildRuleForm(null);
      setEditingChildRule(null);
      fetchChildRules(showChildRuleForm);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save child pricing rule",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChildRule = async (ruleId, roomTypeId) => {
    if (!window.confirm('Delete this child pricing rule?')) return;
    
    try {
      await childPricingApi.delete(ruleId, token);
      toast({ title: "Success", description: "Rule deleted" });
      fetchChildRules(roomTypeId);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete rule",
        variant: "destructive"
      });
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-room-types">
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Room Types & Billing</h1>
            <p className="body-small mt-1" style={{ color: 'var(--text-secondary)' }}>
              Manage room types with their base rates and child pricing rules
            </p>
          </div>
          <Button className="btn-primary flex items-center gap-2" onClick={handleCreate} data-testid="add-room-type-btn">
            <Plus size={16} />
            Add Room Type
          </Button>
        </div>

        {/* Room Type Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) handleCancel(); }}>
          <DialogContent className="sm:max-w-2xl" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
            <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
              <DialogTitle>{editing ? 'Edit Room Type' : 'Create New Room Type'}</DialogTitle>
              <DialogDescription>Define the room type configuration and rates.</DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Room Type Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Deluxe, Standard, Suite"
                    style={{ borderRadius: 0 }}
                    data-testid="room-type-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max_adults">Maximum Adults *</Label>
                  <Input
                    id="max_adults"
                    type="number"
                    min="1"
                    value={formData.max_adults}
                    onChange={(e) => {
                      const maxAdults = parseInt(e.target.value) || 1;
                      setFormData({ 
                        ...formData, 
                        max_adults: e.target.value,
                        // Ensure max_occupancy is at least max_adults
                        max_occupancy: Math.max(maxAdults, parseInt(formData.max_occupancy) || maxAdults)
                      });
                    }}
                    required
                    style={{ borderRadius: 0 }}
                    data-testid="max-adults-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Maximum adults allowed (bed capacity)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="max_occupancy">Total Max Occupancy *</Label>
                  <Input
                    id="max_occupancy"
                    type="number"
                    min={formData.max_adults || 1}
                    value={formData.max_occupancy}
                    onChange={(e) => setFormData({ ...formData, max_occupancy: e.target.value })}
                    required
                    style={{ borderRadius: 0 }}
                    data-testid="max-occupancy-input"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Total occupants including children (must be ≥ max adults)
                  </p>
                </div>
                <div className="flex items-end pb-6">
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <strong>Example:</strong> Max Adults = 2, Total Occupancy = 4<br/>
                    → Allows 2 adults + 2 children, or 1 adult + 3 children
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full p-3 border"
                  style={{ borderColor: 'var(--border-medium)', borderRadius: 0, fontFamily: "'Inter', sans-serif", fontSize: '14px' }}
                  placeholder="Optional description of this room type"
                />
              </div>

              <div>
                <Label>Room Type Image</Label>
                <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Upload a file or paste a URL — shown on the landing page</p>
                
                {/* Image preview (when we have one) */}
                {formData.image_url ? (
                  <div className="mb-3 relative group" style={{ border: '1px solid var(--border-light)', overflow: 'hidden' }}>
                    <img
                      src={formData.image_url}
                      alt="Room type preview"
                      className="w-full h-44 object-cover"
                      onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load image'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                      title="Remove image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : null}

                {/* Upload / URL input row */}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={imageFileRef}
                    className="hidden"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleImageUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageFileRef.current?.click()}
                    disabled={imageUploading}
                    style={{ borderRadius: 0 }}
                    className="flex items-center gap-2"
                  >
                    {imageUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {imageUploading ? 'Uploading…' : 'Upload Image'}
                  </Button>
                  <span className="text-xs self-center" style={{ color: 'var(--text-secondary)' }}>or</span>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    style={{ borderRadius: 0 }}
                    className="flex-1"
                    data-testid="room-type-image-input"
                  />
                </div>
                {imageUploadError && (
                  <p className="text-xs mt-1" style={{ color: '#dc2626' }}>{imageUploadError}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="base_rate_single">Base Rate (Single Adult) *</Label>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-secondary)' }} />
                    <Input
                      id="base_rate_single"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.base_rate_single}
                      onChange={(e) => setFormData({ ...formData, base_rate_single: e.target.value })}
                      required
                      className="pl-10"
                      style={{ borderRadius: 0 }}
                      data-testid="base-rate-input"
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Per night rate for first adult</p>
                </div>
                <div>
                  <Label htmlFor="extra_adult_rate">Extra Adult Rate *</Label>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-secondary)' }} />
                    <Input
                      id="extra_adult_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.extra_adult_rate}
                      onChange={(e) => setFormData({ ...formData, extra_adult_rate: e.target.value })}
                      required
                      className="pl-10"
                      style={{ borderRadius: 0 }}
                      data-testid="extra-adult-rate-input"
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Per night charge for each additional adult</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Label htmlFor="is_active" className="cursor-pointer">Active (available for booking)</Label>
              </div>

              <div>
                <Label htmlFor="sort_order">Display Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  style={{ borderRadius: 0, maxWidth: '120px' }}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Lower numbers display first on the website</p>
              </div>

              </div>

              <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                <Button type="button" variant="outline" onClick={handleCancel} style={{ borderRadius: 0 }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-room-type-btn">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {editing ? 'Update Room Type' : 'Create Room Type'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Room Types List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : roomTypes.length === 0 ? (
          <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
              No room types found. Create your first room type to start defining billing rules.
            </p>
            <Button className="btn-primary" onClick={handleCreate}>
              <Plus size={16} className="mr-2" />
              Add Your First Room Type
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="room-types-list">
            {roomTypes.map((roomType) => (
              <div
                key={roomType.id}
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-light)',
                  opacity: roomType.is_active ? 1 : 0.6
                }}
                data-testid={`room-type-${roomType.id}`}
              >
                {/* Room Type Header */}
                <div className="p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="heading-3">{roomType.name}</h3>
                      {!roomType.is_active && (
                        <span className="px-2 py-1 text-xs" style={{ backgroundColor: '#ef4444', color: 'white' }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    {roomType.description && (
                      <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {roomType.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2">
                      <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <DollarSign size={14} />
                        Base: ${roomType.base_rate_single}/night
                      </span>
                      <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Users size={14} />
                        +${roomType.extra_adult_rate}/extra adult
                      </span>
                      <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Users size={14} />
                        Max {roomType.max_adults} adults
                      </span>
                      <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Baby size={14} />
                        {roomType.max_occupancy || roomType.max_adults} total occupants
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => toggleExpand(roomType.id)}
                      style={{ borderRadius: 0 }}
                    >
                      <Baby size={16} className="mr-1" />
                      Child Rules
                      {expandedType === roomType.id ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={() => handleEdit(roomType)}
                      style={{ borderRadius: 0 }}
                      data-testid={`edit-room-type-${roomType.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={() => handleDelete(roomType.id)}
                      style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                      data-testid={`delete-room-type-${roomType.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                {/* Child Pricing Rules Section */}
                {expandedType === roomType.id && (
                  <div className="px-6 pb-6 pt-2" style={{ borderTop: '1px solid var(--border-light)' }}>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Child Pricing Rules</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAddChildRule(roomType.id)}
                        style={{ borderRadius: 0 }}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Rule
                      </Button>
                    </div>

                    {/* Child Rule Form Dialog */}
                    <Dialog open={showChildRuleForm === roomType.id} onOpenChange={(open) => { if (!open) handleCancelChildRule(); }}>
                      <DialogContent className="sm:max-w-xl" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <DialogTitle>{editingChildRule ? 'Edit Child Pricing Rule' : 'Add Child Pricing Rule'}</DialogTitle>
                          <DialogDescription>Set age-based pricing for {roomType.name}.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmitChildRule} className="flex flex-col flex-1 overflow-hidden">
                          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Min Age</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={childRuleForm.min_age}
                                  onChange={(e) => setChildRuleForm({ ...childRuleForm, min_age: e.target.value })}
                                  style={{ borderRadius: 0 }}
                                />
                              </div>
                              <div>
                                <Label>Max Age</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={childRuleForm.max_age}
                                  onChange={(e) => setChildRuleForm({ ...childRuleForm, max_age: e.target.value })}
                                  style={{ borderRadius: 0 }}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Pricing Type</Label>
                                <select
                                  value={childRuleForm.pricing_type}
                                  onChange={(e) => setChildRuleForm({ ...childRuleForm, pricing_type: e.target.value })}
                                  className="w-full h-10 px-3 border"
                                  style={{ borderColor: 'var(--border-medium)', borderRadius: 0 }}
                                >
                                  {PRICING_TYPES.map(pt => (
                                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <Label>Rate Value ($)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={childRuleForm.rate_value}
                                  onChange={(e) => setChildRuleForm({ ...childRuleForm, rate_value: e.target.value })}
                                  disabled={childRuleForm.pricing_type === 'FREE' || childRuleForm.pricing_type === 'NOT_ALLOWED' || childRuleForm.pricing_type === 'ADULT_RATE'}
                                  style={{ borderRadius: 0 }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                            <Button type="button" size="sm" variant="outline" onClick={handleCancelChildRule} style={{ borderRadius: 0 }}>
                              Cancel
                            </Button>
                            <Button type="submit" size="sm" className="btn-primary" disabled={saving}>
                              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                              {editingChildRule ? 'Update' : 'Add'} Rule
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>

                    {/* Child Rules List */}
                    {childRules[roomType.id]?.length > 0 ? (
                      <div className="space-y-2">
                        {childRules[roomType.id].map(rule => (
                          <div 
                            key={rule.id} 
                            className="flex items-center justify-between p-3"
                            style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}
                          >
                            <div>
                              <span className="font-medium">Ages {rule.min_age}-{rule.max_age}:</span>
                              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>
                                {rule.pricing_type === 'FREE' && 'Free'}
                                {rule.pricing_type === 'FIXED' && `$${rule.rate_value} per night`}
                                {rule.pricing_type === 'ADULT_RATE' && 'Extra adult rate'}
                                {rule.pricing_type === 'NOT_ALLOWED' && <span style={{ color: '#ef4444' }}>Not allowed</span>}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleEditChildRule(rule)}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleDeleteChildRule(rule.id, roomType.id)}
                                style={{ color: '#ef4444' }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        No child pricing rules defined. Children may not be allowed for this room type.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRoomTypesPage;
